import type { TaskType } from '@api';
import { ActionsCell, LinkableEntity, LinkableEntityList } from '@common';
import { Text } from '@mantine/core';
import { DataTableColumn } from 'mantine-datatable';
import { useTranslation } from 'react-i18next';
import {
  ContextLinkAction,
  TaskPriorityColumnFilter,
  TaskStatusColumnFilter,
} from './components';

const TRANS_PATH = 'TABLE.COLUMNS';

type UseTasksOverviewTableColumnsArgsType = {
  priorityFilter: string | undefined;
  setPriorityFilter: (value: string | undefined) => void;
  statusFilter: string | undefined;
  setStatusFilter: (value: string | undefined) => void;
  onView: (task: TaskType) => void;
  onClose: (taskId: string) => void;
  onReopen: (taskId: string) => void;
  userCanWriteTasks: boolean;
};

type UseTasksOverviewTableColumnsReturnType = {
  columns: DataTableColumn<TaskType>[];
};

export const useTasksOverviewTableColumns = ({
  priorityFilter,
  setPriorityFilter,
  statusFilter,
  setStatusFilter,
  onView,
  onClose,
  onReopen,
  userCanWriteTasks,
}: UseTasksOverviewTableColumnsArgsType): UseTasksOverviewTableColumnsReturnType => {
  const { t } = useTranslation(['tasks', 'common']);
  const noDataPlaceholder = t('NO_DATA_PLACEHOLDER', { ns: 'common' });

  const columns: DataTableColumn<TaskType>[] = [
    {
      accessor: 'title',
      title: t(`${TRANS_PATH}.TITLE`),
    },
    {
      accessor: 'priority',
      title: t(`${TRANS_PATH}.PRIORITY`),
      sortable: true,
      render: ({ priority }) => t(`PRIORITY.${priority}`, { ns: 'tasks' }),
      filter: ({ close }) => (
        <TaskPriorityColumnFilter
          value={priorityFilter}
          onChange={setPriorityFilter}
          onClose={close}
        />
      ),
      filtering: Boolean(priorityFilter),
    },
    {
      accessor: 'status',
      title: t(`${TRANS_PATH}.STATUS`),
      sortable: true,
      render: ({ status }) => t(`STATUS.${status}`, { ns: 'tasks' }),
      filter: ({ close }) => (
        <TaskStatusColumnFilter
          value={statusFilter}
          onChange={setStatusFilter}
          onClose={close}
        />
      ),
      filtering: Boolean(statusFilter),
    },
    {
      accessor: 'description',
      title: t(`${TRANS_PATH}.DESCRIPTION`),
      render: ({ description }) => (
        <Text lineClamp={2} maw={280}>
          {description}
        </Text>
      ),
    },
    {
      accessor: 'claimedBy',
      title: t(`${TRANS_PATH}.CLAIMED_BY`),
      render: ({ claimedBy }) =>
        claimedBy ? (
          <LinkableEntity linkableEntity={claimedBy} />
        ) : (
          noDataPlaceholder
        ),
    },
    {
      accessor: 'createdBy',
      title: t(`${TRANS_PATH}.CREATED_BY`),
      render: ({ createdBy }) =>
        createdBy ? (
          <LinkableEntity linkableEntity={createdBy} />
        ) : (
          noDataPlaceholder
        ),
    },
    {
      accessor: 'audience',
      title: t(`${TRANS_PATH}.AUDIENCE`),
      render: ({ audience }) =>
        audience.length > 0 ? (
          <LinkableEntityList entities={audience} />
        ) : (
          noDataPlaceholder
        ),
    },
    {
      accessor: 'actions',
      title: t(`${TRANS_PATH}.ACTIONS`),
      titleStyle: { textAlign: 'center' },
      render: (record) => (
        <ActionsCell.Root>
          <ActionsCell.View onClick={() => onView(record)} />
          {userCanWriteTasks &&
            (record.status === 'OPEN' ? (
              <ActionsCell.Close onClick={() => onClose(record.id)} />
            ) : (
              <ActionsCell.Reopen onClick={() => onReopen(record.id)} />
            ))}
          <ContextLinkAction to={record.context} disabled={!record.context} />
        </ActionsCell.Root>
      ),
    },
  ];

  return { columns };
};
